import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TILE } from '../../shared/geo.js';
import { hash2 } from '../../shared/rng.js';
import { pointInPolygon } from '../../shared/clip.js';
import { loadModel, loadTree } from './models.js';

// The model for each kind, scaled to the real tree, in the art bible's colours
// (kinds: 0 eucalyptus, 1 mango, 2 cypress, 3 palm, 4 forest tree). The Kenney kit shapes are
// vertex-coloured; the nature kit's trees and our own Blender eucalyptus and oil palm (textured:
// true) carry their leaves as textured cards, tinted to the species, and draw with two materials.
const MODELS = [
  ['tree-eucalyptus-b', { scale: 1, tint: '#f4f4f0' }, true],
  ['tree-common-3', { scale: 1.05, tint: '#7fb45f' }, true],
  ['tree-pine-2', { scale: 1.4, tint: '#86b27a' }, true],
  ['tree-palm-b', { scale: 1, tint: '#f4f4f0' }, true],
  ['tree-common-5', { scale: 1.15, tint: '#6ea85a' }, true],
];
const MATS = [null, null, null, null, null]; // the materials of a textured kind, else the shared one
const LIVE = [new Set(), new Set(), new Set(), new Set(), new Set()]; // the instanced meshes drawing each kind

const CELL = 8; // metres; one tree at most per cell
const SHARED_PROGRAM = 1025; // instances from which the new renderer uses one shared shader
const N = TILE / CELL;

// chance of a tree per cell, by land-use kind (see the map builder's LAND table)
const DENSITY = { 0: 0.42, 1: 0.14, 2: 0.012, 3: 0.012, 4: 0.05, 5: 0.015, 7: 0.05, 9: 0.03 };
const OPEN_GROUND = 0.016;
const BESIDE_HOUSE = 0.07;

function tinted(geometry, hex) {
  const c = new THREE.Color(hex);
  const n = geometry.attributes.position.count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return geometry.index ? geometry.toNonIndexed() : geometry;
}

function part(geometry, hex, x, y, z) {
  geometry.translate(x, y, z);
  return tinted(geometry, hex);
}

// Each kind is one small merged shape, about as tall as the real tree in metres.
function makeKinds() {
  const trunk = '#6b4a30';
  return [
    // eucalyptus: tall and narrow, the tree of the Bamenda hills
    mergeGeometries([
      part(new THREE.CylinderGeometry(0.16, 0.26, 9, 5), '#b9a58c', 0, 4.5, 0),
      part(new THREE.IcosahedronGeometry(2.1, 0).scale(0.8, 1.9, 0.8), '#5f8f5a', 0, 11, 0),
    ]),
    // mango or avocado: broad round crown, found in compounds
    mergeGeometries([
      part(new THREE.CylinderGeometry(0.28, 0.4, 3.2, 5), trunk, 0, 1.6, 0),
      part(new THREE.IcosahedronGeometry(3.1, 0).scale(1, 0.78, 1), '#2f7d3a', 0, 4.9, 0),
    ]),
    // cypress: a dark spire, planted along fences
    mergeGeometries([
      part(new THREE.CylinderGeometry(0.14, 0.2, 1.4, 5), trunk, 0, 0.7, 0),
      part(new THREE.ConeGeometry(1.25, 7.5, 6), '#2a5a3c', 0, 5, 0),
    ]),
    // raffia or oil palm: bare trunk and a burst of fronds
    mergeGeometries([
      part(new THREE.CylinderGeometry(0.2, 0.3, 5.4, 5), '#7a6248', 0, 2.7, 0),
      part(new THREE.ConeGeometry(2.9, 1.5, 7).rotateX(Math.PI), '#3f8a45', 0, 5.8, 0),
      part(new THREE.ConeGeometry(1.9, 1.6, 7), '#4fa052', 0, 6.6, 0),
    ]),
    // forest tree: a tall broadleaf
    mergeGeometries([
      part(new THREE.CylinderGeometry(0.3, 0.45, 6, 5), trunk, 0, 3, 0),
      part(new THREE.IcosahedronGeometry(3.4, 0).scale(1, 1.05, 1), '#24693a', 0, 8.2, 0),
    ]),
  ];
}

let KINDS = null;
let MATERIAL = null;
const dummy = new THREE.Object3D();

// What each 8 m cell of a tile holds, for planting: 255 blocked (road, building, water, pitch),
// otherwise land-use kind + 1 (0 = open ground); and whether it lies beside a house.
export function treeRaster(data) {
  const cells = new Uint8Array(N * N);
  const beside = new Uint8Array(N * N);
  if (!data) return { cells, beside };
  for (const l of data.l) {
    if (DENSITY[l.k] === undefined && l.k !== 8 && l.k !== 10 && l.k !== 6) continue;
    let x0 = Infinity;
    let x1 = -Infinity;
    let z0 = Infinity;
    let z1 = -Infinity;
    for (let i = 0; i < l.p.length; i += 2) {
      x0 = Math.min(x0, l.p[i]);
      x1 = Math.max(x1, l.p[i]);
      z0 = Math.min(z0, l.p[i + 1]);
      z1 = Math.max(z1, l.p[i + 1]);
    }
    const c0 = Math.max(0, Math.floor(x0 / (CELL * 10)));
    const c1 = Math.min(N - 1, Math.floor(x1 / (CELL * 10)));
    const r0 = Math.max(0, Math.floor(z0 / (CELL * 10)));
    const r1 = Math.min(N - 1, Math.floor(z1 / (CELL * 10)));
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        if (pointInPolygon((c + 0.5) * CELL * 10, (r + 0.5) * CELL * 10, l.p)) cells[r * N + c] = DENSITY[l.k] === undefined ? 255 : l.k + 1;
      }
    }
  }
  const block = (x, z, reach) => {
    const c0 = Math.max(0, Math.floor((x - reach) / CELL));
    const c1 = Math.min(N - 1, Math.floor((x + reach) / CELL));
    const r0 = Math.max(0, Math.floor((z - reach) / CELL));
    const r1 = Math.min(N - 1, Math.floor((z + reach) / CELL));
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) cells[r * N + c] = 255;
  };
  const along = (p, half) => {
    for (let i = 2; i < p.length; i += 2) {
      const ax = p[i - 2] / 10;
      const az = p[i - 1] / 10;
      const bx = p[i] / 10;
      const bz = p[i + 1] / 10;
      const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / 4));
      for (let s = 0; s <= steps; s++) block(ax + ((bx - ax) * s) / steps, az + ((bz - az) * s) / steps, half);
    }
  };
  for (const r of data.r) along(r.p, r.w / 2 + 1.5);
  for (const w of data.w) along(w.p, w.w / 2 + 1);
  for (const a of data.a) along(a.p.concat(a.p.slice(0, 2)), 4);
  for (const b of data.b) {
    let x0 = Infinity;
    let x1 = -Infinity;
    let z0 = Infinity;
    let z1 = -Infinity;
    for (let i = 0; i < b.p.length; i += 2) {
      x0 = Math.min(x0, b.p[i] / 10);
      x1 = Math.max(x1, b.p[i] / 10);
      z0 = Math.min(z0, b.p[i + 1] / 10);
      z1 = Math.max(z1, b.p[i + 1] / 10);
    }
    const c0 = Math.max(0, Math.floor((x0 - 1) / CELL));
    const c1 = Math.min(N - 1, Math.floor((x1 + 1) / CELL));
    const r0 = Math.max(0, Math.floor((z0 - 1) / CELL));
    const r1 = Math.min(N - 1, Math.floor((z1 + 1) / CELL));
    for (let r = Math.max(0, r0 - 1); r <= Math.min(N - 1, r1 + 1); r++) {
      for (let c = Math.max(0, c0 - 1); c <= Math.min(N - 1, c1 + 1); c++) {
        if (r >= r0 && r <= r1 && c >= c0 && c <= c1) cells[r * N + c] = 255;
        else beside[r * N + c] = 1;
      }
    }
  }
  return { cells, beside };
}

// Where the trees stand in part of a tile (8 m cells c0..c1 across, r0..r1 down, inclusive):
// one flat list per kind of [x, y, z, scale, yaw] with x and z local to the tile's corner.
export function treeLists(raster, tx, tz, heightmap, c0 = 0, c1 = N - 1, r0 = 0, r1 = N - 1) {
  const ox = tx * TILE;
  const oz = tz * TILE;
  const { cells, beside } = raster;
  const lists = [[], [], [], [], []];
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      const cell = cells[r * N + c];
      if (cell === 255) continue;
      const wx = ox + c * CELL;
      const wz = oz + r * CELL;
      let chance = cell === 0 ? OPEN_GROUND : DENSITY[cell - 1];
      if (beside[r * N + c]) chance = Math.max(chance, BESIDE_HOUSE);
      const roll = hash2(wx, wz, 41);
      if (roll > chance) continue;
      const x = wx + hash2(wx, wz, 42) * CELL;
      const z = wz + hash2(wx, wz, 43) * CELL;
      const y = heightmap.heightAt(x, z);
      const pick = hash2(wx, wz, 44);
      let kind;
      if (cell === 1) kind = pick < 0.6 ? 4 : pick < 0.85 ? 0 : 3; // forest
      else if (beside[r * N + c] || cell === 5) kind = pick < 0.45 ? 1 : pick < 0.7 ? 3 : pick < 0.88 ? 2 : 0; // compounds
      else if (y > 1500) kind = pick < 0.7 ? 0 : 2; // high ground is eucalyptus country
      else kind = pick < 0.45 ? 0 : pick < 0.7 ? 1 : pick < 0.88 ? 3 : 2;
      lists[kind].push(x - ox, y, z - oz, 0.75 + hash2(wx, wz, 45) * 0.6, hash2(wx, wz, 46) * Math.PI * 2);
    }
  }
  return lists;
}

// The one material every tree is drawn with.
export function treeMaterial() {
  if (!KINDS) {
    KINDS = makeKinds();
    MATERIAL = new THREE.MeshLambertMaterial({ vertexColors: true });
    // swap in the kit models when they arrive, on every tree already standing too
    // ?trees=kenney keeps the vertex-coloured kit shapes for every kind, for measuring
    let plain = false;
    try {
      plain = new URLSearchParams(location.search).get('trees') === 'kenney';
    } catch {
      plain = false;
    }
    const KENNEY = { 0: ['tree-eucalyptus', { scale: 7.2, colours: { leafsGreen: '#6f8a66', woodBark: '#d8cdbb' } }], 3: ['tree-palm', { scale: 5.2, colours: { leafsGreen: '#3f7a3a', woodBark: '#7a6248' } }], 1: ['tree-mango', { scale: 6.5, colours: { leafsGreen: '#2f5f32', woodBark: '#6b4a30' } }], 2: ['tree-cypress', { scale: 6.0, colours: { leafsDark: '#2a5a3c', woodBarkDark: '#5a4632' } }], 4: ['tree-forest', { scale: 6.4, colours: { leafsGreen: '#24693a', woodBark: '#6b4a30', _defaultMat: '#2f5f32' } }] };
    MODELS.forEach(([name, options, textured], kind) => {
      if (plain && textured) {
        [name, options] = KENNEY[kind];
        textured = false;
      }
      const arrived = (geometry, materials) => {
        if (!geometry) return;
        KINDS[kind] = geometry;
        MATS[kind] = materials || null;
        IMPOSTORS[kind] = null;
        bakeImpostor(kind);
        for (const mesh of LIVE[kind]) {
          if (mesh.parent) {
            mesh.geometry = geometry;
            if (materials) mesh.material = materials;
            mesh.computeBoundingSphere();
          } else LIVE[kind].delete(mesh);
        }
      };
      if (textured) loadTree(name, options).then((t) => t && arrived(t.geometry, t.materials));
      else loadModel(name, options).then((geometry) => arrived(geometry, null));
    });
  }
  return MATERIAL;
}

// Far trees are cards: one picture of each kind rendered from the side when its model arrives,
// drawn on a quad that turns to face the camera (in the vertex shader, about the trunk). A cell's
// trees exist twice, as the full shapes and as cards sharing the same instance matrices; the cell
// shows one or the other by its distance (setTreeLod). Legacy renderer only: the cards need the
// renderer to bake with and a shader hook the node renderer does not have.
const IMPOSTORS = [null, null, null, null, null]; // kind -> { material, quad } once baked
const LIVE_FAR = [new Set(), new Set(), new Set(), new Set(), new Set()];
let bakeRenderer = null;
export function setTreeRenderer(renderer) {
  bakeRenderer = renderer;
  treeMaterial();
  for (let kind = 0; kind < KINDS.length; kind++) if (!IMPOSTORS[kind]) bakeImpostor(kind);
}

const BILLBOARD_VERTEX = `
  vec4 mvPosition = vec4( transformed, 1.0 );
  #ifdef USE_INSTANCING
    vec3 ip = ( modelMatrix * vec4( instanceMatrix[ 3 ].xyz, 1.0 ) ).xyz;
    float s = length( instanceMatrix[ 0 ].xyz );
    vec3 right = normalize( cross( vec3( 0.0, 1.0, 0.0 ), toCam ) );
    vec3 world = ip + right * ( transformed.x * s ) + vec3( 0.0, transformed.y * s, 0.0 );
    mvPosition = viewMatrix * vec4( world, 1.0 );
  #else
    mvPosition = modelViewMatrix * mvPosition;
  #endif
  gl_Position = projectionMatrix * mvPosition;
`;
// the direction to the camera, flat, worked out before the normal and the position need it
const BILLBOARD_TOCAM = `
  #ifdef USE_INSTANCING
    toCam = cameraPosition - ( modelMatrix * vec4( instanceMatrix[ 3 ].xyz, 1.0 ) ).xyz;
    toCam.y = 0.0;
    toCam = normalize( toCam );
  #endif
  #include <beginnormal_vertex>
`;

// Renders a kind's shape from the side into a texture, and makes the card's material and quad.
function bakeImpostor(kind) {
  if (!bakeRenderer || IMPOSTORS[kind] || !KINDS[kind]) return;
  const geometry = KINDS[kind];
  geometry.computeBoundingBox();
  const b = geometry.boundingBox;
  const width = Math.max(b.max.x - b.min.x, b.max.z - b.min.z);
  const height = b.max.y - b.min.y;
  const W = 256;
  const H = 512;
  const rt = new THREE.WebGLRenderTarget(W, H, { format: THREE.RGBAFormat, type: THREE.UnsignedByteType, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter });
  rt.texture.colorSpace = THREE.LinearSRGBColorSpace;
  // the shape's own colours, unlit: the card is lit where it stands
  const source = MATS[kind] || MATERIAL;
  const basic = (m) => new THREE.MeshBasicMaterial({ vertexColors: true, map: m.map || null, alphaTest: m.alphaTest || 0, side: THREE.DoubleSide });
  const flat = Array.isArray(source) ? source.map(basic) : basic(source);
  const mesh = new THREE.Mesh(geometry, flat);
  const scene = new THREE.Scene();
  scene.add(mesh);
  const cam = new THREE.OrthographicCamera(-width / 2, width / 2, b.max.y, b.min.y, -width * 2, width * 2);
  cam.position.set(0, 0, width);
  cam.lookAt(0, 0, 0);
  const renderer = bakeRenderer;
  const oldTarget = renderer.getRenderTarget();
  const oldColor = new THREE.Color();
  renderer.getClearColor(oldColor);
  const oldAlpha = renderer.getClearAlpha();
  renderer.setRenderTarget(rt);
  renderer.setClearColor(0x000000, 0);
  renderer.clear();
  renderer.render(scene, cam);
  renderer.setRenderTarget(oldTarget);
  renderer.setClearColor(oldColor, oldAlpha);
  for (const m of Array.isArray(flat) ? flat : [flat]) m.dispose();
  const material = new THREE.MeshLambertMaterial({ map: rt.texture, alphaTest: 0.5, side: THREE.DoubleSide });
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'vec3 toCam;\nvoid main() {')
      .replace('#include <beginnormal_vertex>', BILLBOARD_TOCAM)
      // the card faces the camera, so its normal does too, tipped up a little for the sun
      .replace('#include <defaultnormal_vertex>', '#include <defaultnormal_vertex>\n#ifdef USE_INSTANCING\n transformedNormal = normalize( ( viewMatrix * vec4( normalize( toCam + vec3( 0.0, 0.6, 0.0 ) ), 0.0 ) ).xyz );\n#endif')
      .replace('#include <project_vertex>', BILLBOARD_VERTEX);
  };
  const quad = new THREE.PlaneGeometry(width, height).translate(0, height / 2 + b.min.y, 0);
  IMPOSTORS[kind] = { material, quad };
  for (const far of LIVE_FAR[kind]) {
    if (!far.parent) {
      LIVE_FAR[kind].delete(far);
      continue;
    }
    far.geometry = quad;
    far.material = material;
    far.computeBoundingSphere();
  }
}

// Shows a cell's trees as full shapes (far false) or cards (far true, when the kind has cards).
export function setTreeLod(group, far) {
  const lod = group.userData.trees;
  if (!lod) return;
  for (const [near, card] of lod) {
    const cards = far && card.geometry !== PLACEHOLDER;
    near.visible = !cards;
    card.visible = cards;
  }
}
const PLACEHOLDER = new THREE.BufferGeometry();

// The trees of treeLists as one InstancedMesh per kind (and one of cards per kind sharing its
// matrices) in a Group at world (ox, oz), or null if there are none.
export function treeMeshes(lists, ox, oz) {
  treeMaterial();
  let group = null;
  lists.forEach((list, kind) => {
    const count = list.length / 5;
    if (count === 0) return;
    // Room for 1025 even when far fewer stand here: past 1024 matrices the new renderer passes
    // them as vertex attributes, whose shader is the same for every mesh. Below that it bakes a
    // uniform buffer of that exact size and name into the shader, so every tile or cell of trees
    // would link its own program - measured 8 October 2026 as a stall per cell while driving.
    const mesh = new THREE.InstancedMesh(KINDS[kind], MATS[kind] || MATERIAL, Math.max(count, SHARED_PROGRAM));
    mesh.count = count;
    for (let i = 0; i < count; i++) {
      dummy.position.set(list[i * 5], list[i * 5 + 1] - 0.15, list[i * 5 + 2]);
      dummy.scale.setScalar(list[i * 5 + 3]);
      dummy.rotation.set(0, list[i * 5 + 4], 0);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = true;
    mesh.computeBoundingSphere();
    LIVE[kind].add(mesh);
    if (!group) {
      group = new THREE.Group();
      group.position.set(ox, 0, oz);
      group.userData.trees = [];
    }
    group.add(mesh);
    if (bakeRenderer) {
      const imp = IMPOSTORS[kind];
      const card = new THREE.InstancedMesh(imp ? imp.quad : PLACEHOLDER, imp ? imp.material : MATERIAL, Math.max(count, SHARED_PROGRAM));
      card.instanceMatrix = mesh.instanceMatrix;
      card.count = count;
      card.castShadow = false;
      card.receiveShadow = false;
      card.frustumCulled = false;
      card.visible = false;
      LIVE_FAR[kind].add(card);
      group.add(card);
      group.userData.trees.push([mesh, card]);
    }
  });
  return group;
}

// Plants a tile's trees: dense in forest, scattered in compounds and on open
// ground, never on a road, a building or water. Returns a Group, or null if empty.
export function buildTrees(data, tx, tz, heightmap) {
  return treeMeshes(treeLists(treeRaster(data), tx, tz, heightmap), tx * TILE, tz * TILE);
}
